import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, Save, Trash2 } from "lucide-react";
import { adminGetPattern, adminGetPatterns, adminEditPattern, adminPatternRemoveCase, adminApprovePattern, adminRejectPattern, adminMergePattern } from "@/lib/apiClient";
import AdminNav from "@/components/AdminNav";
import { toast } from "sonner";

const inputCls = "w-full h-11 px-3 border-2 border-ink outline-none focus:shadow-[3px_3px_0_0_#0A0A0C] bg-white";
const areaCls = "w-full p-3 border-2 border-ink outline-none focus:shadow-[3px_3px_0_0_#0A0A0C] bg-white min-h-[80px]";

export default function AdminPatternReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [others, setOthers] = useState([]);
  const [mergeId, setMergeId] = useState("");

  const load = () => adminGetPattern(id).then(setP).catch(() => { toast.error("Pattern not found"); navigate("/admin/patterns"); });
  useEffect(() => { load(); adminGetPatterns("published").then(setOthers); }, [id]);

  if (!p) return <div><AdminNav /><div className="max-w-5xl mx-auto px-4 py-20 text-ink/40">Loading…</div></div>;

  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const save = async () => {
    await adminEditPattern(id, { name: p.name, description: p.description, why_interesting: p.why_interesting, underlying_shift: p.underlying_shift });
    toast.success("Saved"); load();
  };
  const removeCase = async (slug) => { const u = await adminPatternRemoveCase(id, slug); setP((x) => ({ ...u, cases: (x.cases || []).filter((c) => c.slug !== slug) })); toast.success("Case removed"); };
  const approve = async () => { await adminApprovePattern(id); toast.success("Published"); navigate("/admin/patterns"); };
  const reject = async () => { await adminRejectPattern(id); toast.success("Rejected"); navigate("/admin/patterns"); };
  const merge = async () => { if (!mergeId) return toast.error("Pick a pattern to merge into"); await adminMergePattern(id, mergeId); toast.success("Merged"); navigate("/admin/patterns"); };

  return (
    <div>
      <AdminNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/admin/patterns" className="group inline-flex items-center gap-2 font-semibold text-sm text-ink/70"><ArrowLeft size={16} /> Back to patterns</Link>
        <div className="flex items-center gap-2 mt-4">
          <span className="eyebrow bg-bryellow text-ink px-2 py-1">{p.status}</span>
          <span className="text-sm text-ink/50">{p.supporting_count} cases · {(p.industries || []).length} industries · {(p.countries || []).length} countries · {p.source_count || 0} sources</span>
        </div>

        <div className="mt-5 space-y-4">
          <div><label className="eyebrow text-ink/60 block mb-1">Pattern name</label><input className={inputCls} value={p.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="pattern-name-input" /></div>
          <div><label className="eyebrow text-ink/60 block mb-1">Short description</label><textarea className={areaCls} value={p.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div><label className="eyebrow text-ink/60 block mb-1">Why this is interesting</label><textarea className={areaCls} value={p.why_interesting || ""} onChange={(e) => set("why_interesting", e.target.value)} /></div>
          <div><label className="eyebrow text-ink/60 block mb-1">Underlying business shift</label><input className={inputCls} value={p.underlying_shift || ""} onChange={(e) => set("underlying_shift", e.target.value)} /></div>
          {p.grouping_reason && <div className="border-2 border-ink/20 bg-secondary/50 p-4"><p className="eyebrow text-ink/50 mb-1">Why these cases were grouped (AI)</p><p className="text-sm text-ink/70">{p.grouping_reason}</p></div>}
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          <button onClick={save} data-testid="pattern-save-button" className="btn-tactile inline-flex items-center gap-2 bg-white border-2 border-ink font-bold px-5 h-11 hover:bg-bryellow"><Save size={16} /> Save</button>
          <button onClick={approve} data-testid="pattern-approve-button" className="btn-tactile inline-flex items-center gap-2 bg-ink text-white font-bold px-5 h-11 hover:bg-cobalt"><Check size={16} /> Approve & Publish</button>
          <button onClick={reject} data-testid="pattern-reject-button" className="btn-tactile inline-flex items-center gap-2 border-2 border-ink font-bold px-5 h-11 hover:bg-brorange hover:text-white"><X size={16} /> Reject</button>
          <div className="flex items-center gap-2">
            <select value={mergeId} onChange={(e) => setMergeId(e.target.value)} data-testid="pattern-merge-select" className="h-11 px-3 border-2 border-ink bg-white font-semibold text-sm">
              <option value="">Merge into…</option>
              {others.filter((o) => o.id !== id).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <button onClick={merge} data-testid="pattern-merge-button" className="btn-tactile border-2 border-ink font-bold px-4 h-11 hover:bg-bryellow">Merge</button>
          </div>
        </div>

        <h2 className="font-display font-extrabold text-2xl uppercase mt-10 mb-3">Supporting cases ({(p.cases || []).length})</h2>
        <p className="text-sm text-ink/50 mb-4">Remove any case that doesn't genuinely demonstrate this underlying change.</p>
        <div className="space-y-2" data-testid="pattern-supporting-cases">
          {(p.cases || []).map((c) => (
            <div key={c.slug} className="border-2 border-ink p-3 flex items-center justify-between gap-3" data-testid={`pattern-case-${c.slug}`}>
              <div className="min-w-0">
                <div className="font-bold truncate">{c.company} <span className="text-ink/40 font-normal">· {c.industry} · {c.country}</span></div>
                <div className="text-sm text-ink/60 truncate">{c.headline}</div>
                {c.underlying_shift && <div className="text-xs text-cobalt mt-0.5 truncate">↳ {c.underlying_shift}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`/case/${c.slug}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-cobalt">View</a>
                <button onClick={() => removeCase(c.slug)} data-testid={`pattern-remove-case-${c.slug}`} className="h-8 w-8 flex items-center justify-center border border-ink/20 hover:bg-brorange hover:text-white"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
